// src/components/VerifyOriginalModal.tsx
// Raw-vs-edit verification flow for media flagged as edited / AI-enhanced.
//
//   intro  →  pick raw  →  uploading  →  matching (poll)  →  result
//                                                             ├─ PASSED  → certified
//                                                             └─ FAILED  → reasons + "Request manual review"
//
// Design matches MediaDetailsModal (dark #141414, white/10 borders,
// emerald/rose/yellow accents, white primary buttons). Self-contained:
// resumes an in-flight verification when reopened.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { apiService } from "../services/api";

type Step =
  | "loading"        // fetching current state on open
  | "intro"          // explain + file picker
  | "uploading"      // raw PUT in progress
  | "matching"       // worker comparing (poll)
  | "passed"
  | "failed"         // verdict + reasons + evidence + request review
  | "reviewPending"  // manual review requested, awaiting admin
  | "reviewDecided"  // admin approved/rejected
  | "noOriginal"     // "I don't have the original" — reason form
  | "noOriginalSent" // reason emailed to support
  | "error";

const NO_ORIGINAL_SUGGESTIONS = [
  "It's a promotional video made for us by an agency",
  "The original was captured on a device I no longer have",
  "It was filmed/edited by a third party / collaborator",
  "The original file was lost",
];

type Props = {
  mediaId: string;
  mediaName?: string;
  mediaKind?: "image" | "video" | "audio";
  onClose: () => void;
  /** Called when the verification (or a review) ends in a registered state. */
  onVerified?: () => void;
  /** Closes the whole modal stack and navigates to the library ("Back to library"). */
  onGoToLibrary?: () => void;
  /** Open straight to the "I don't have the original" reason form. */
  startNoOriginal?: boolean;
};

const VERDICT_COPY: Record<string, { title: string; body: string }> = {
  CONTENT_CHANGED: {
    title: "Content differences detected",
    body: "Too much of this media differs from the original to auto-certify it as a plain enhancement — a region looks added, removed or replaced, or the overall change is above our auto-match limit. Our team can still approve it with a manual review.",
  },
  NOT_DERIVED: {
    title: "Media don't match",
    body: "We couldn't find a relationship between the file you uploaded and this media. Make sure you selected the exact original this was made from.",
  },
  UNCERTAIN: {
    title: "Verification inconclusive",
    body: "The comparison didn't produce a clear result either way. You can request a manual review and our team will look at both files.",
  },
};

/** Auto-match uses a 35%-changed threshold; expose it so the copy stays truthful. */
const AUTO_MATCH_THRESHOLD = 35;

const Spinner = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className="animate-spin inline-block align-middle" aria-hidden>
    <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.15)" strokeWidth="4" />
    <path d="M22 12a10 10 0 00-10-10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
  </svg>
);

export default function VerifyOriginalModal({ mediaId, mediaName, mediaKind = "image", onClose, onVerified, onGoToLibrary, startNoOriginal = false }: Props) {
  // "Back to library" — falls back to just closing if no navigator was provided.
  const goToLibrary = onGoToLibrary ?? onClose;
  const [step, setStep] = useState<Step>(startNoOriginal ? "noOriginal" : "loading");
  const [progress, setProgress] = useState(0);
  const [verification, setVerification] = useState<any | null>(null);
  const [reviewMessage, setReviewMessage] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [noOrigReason, setNoOrigReason] = useState("");
  const [submittingNoOrig, setSubmittingNoOrig] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Guards against attaching a second original while one is already being
  // prepared/uploaded (each attach starts a match attempt).
  const [attaching, setAttaching] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);

  const applyState = useCallback((v: any | null) => {
    setVerification(v);
    if (!v) { setStep("intro"); return; }
    if (v.manualReview) {
      if (v.manualReview.status === "PENDING") { setStep("reviewPending"); return; }
      setStep("reviewDecided");
      if (v.manualReview.status === "APPROVED") onVerified?.();
      return;
    }
    switch (v.status) {
      case "AWAITING_RAW": setStep("intro"); break;
      case "MATCHING": setStep("matching"); break;
      case "PASSED": setStep("passed"); onVerified?.(); break;
      case "FAILED":
      case "ERROR": setStep("failed"); break;
      default: setStep("intro");
    }
  }, [onVerified]);

  // Resume state on open. Skipped when opened straight to the no-original form.
  useEffect(() => {
    mounted.current = true;
    if (startNoOriginal) { return () => { mounted.current = false; if (pollTimer.current) clearTimeout(pollTimer.current); }; }
    (async () => {
      try {
        const v = await apiService.getMatchVerification(mediaId);
        if (mounted.current) applyState(v);
      } catch {
        if (mounted.current) setStep("intro");
      }
    })();
    return () => {
      mounted.current = false;
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, [mediaId, applyState, startNoOriginal]);

  // Poll while matching.
  useEffect(() => {
    if (step !== "matching") return;
    const tick = async () => {
      try {
        const v = await apiService.getMatchVerification(mediaId);
        if (!mounted.current) return;
        if (v && v.status !== "MATCHING") { applyState(v); return; }
      } catch { /* keep polling */ }
      pollTimer.current = setTimeout(tick, 4000);
    };
    pollTimer.current = setTimeout(tick, 4000);
    return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
  }, [step, mediaId, applyState]);

  const pickFile = () => { if (!attaching) fileInput.current?.click(); };

  const handleFile = async (file: File | null) => {
    // Reset the input so picking the same file again later still fires onChange.
    if (fileInput.current) fileInput.current.value = "";
    if (!file) return;
    if (attaching) return;                       // ignore rapid re-attaches
    if (!mediaId || mediaId === "undefined" || mediaId === "null") {
      setErrorMsg("This media isn't ready to verify yet. Reopen it from your library and try again.");
      return;
    }
    setAttaching(true);
    setErrorMsg(null);
    setStep("uploading");
    setProgress(0);
    try {
      await apiService.startMatchRequest(mediaId);
      const { uploadUrl } = await apiService.getRawUploadUrl(mediaId, file.type);
      await apiService.uploadRawToS3(uploadUrl, file, setProgress);
      await apiService.confirmRawUploaded(mediaId);
      if (mounted.current) setStep("matching");
    } catch (e: any) {
      if (!mounted.current) return;
      setErrorMsg(e?.response?.data?.error || e?.message || "Upload failed. Please try again.");
      setStep("intro");
    } finally {
      if (mounted.current) setAttaching(false);
    }
  };

  const submitReview = async () => {
    setSubmittingReview(true);
    try {
      await apiService.requestManualReview(mediaId, reviewMessage.trim() || undefined);
      if (mounted.current) setStep("reviewPending");
    } catch (e: any) {
      if (mounted.current) setErrorMsg(e?.response?.data?.error || "Could not request review.");
    } finally {
      if (mounted.current) setSubmittingReview(false);
    }
  };

  const submitNoOriginal = async () => {
    if (!noOrigReason.trim()) return;
    if (!mediaId || mediaId === "undefined" || mediaId === "null") {
      setErrorMsg("This media isn't ready yet. Reopen it from your library and try again.");
      return;
    }
    setSubmittingNoOrig(true); setErrorMsg(null);
    try {
      await apiService.submitNoOriginal(mediaId, noOrigReason.trim());
      if (mounted.current) setStep("noOriginalSent");
    } catch (e: any) {
      if (mounted.current) setErrorMsg(e?.response?.data?.error || "Could not send your request.");
    } finally {
      if (mounted.current) setSubmittingNoOrig(false);
    }
  };

  const verdict = verification?.verdict as string | undefined;
  const verdictCopy = (verdict && VERDICT_COPY[verdict]) || VERDICT_COPY.UNCERTAIN;
  const evidence = verification?.evidence || {};

  // Match figures + retry budget (from the backend, so all platforms agree).
  const changedPct: number | null =
    verification?.percentChanged != null ? Number(verification.percentChanged) : null;
  const matchPct: number | null = changedPct != null ? Math.max(0, Math.min(100, 100 - changedPct)) : null;
  // Backend-provided, accurate, scenario-specific reasons (preferred over the
  // generic verdict copy). Filter out any empty entries.
  const serverReasons: string[] = Array.isArray(verification?.reasons)
    ? verification.reasons.filter((r: any) => typeof r === "string" && r.trim())
    : [];
  const attemptCount: number = Number(verification?.attemptCount ?? 0);
  const maxAttempts: number = Number(verification?.maxAttempts ?? 3);
  const retriesLeft: number =
    verification?.retriesLeft != null
      ? Number(verification.retriesLeft)
      : Math.max(0, maxAttempts - attemptCount);

  // Media-kind-aware copy so a video/audio user never sees photo-only wording.
  const kindNoun = mediaKind === "video" ? "video" : mediaKind === "audio" ? "audio" : "photo";
  const editExamples =
    mediaKind === "audio" ? "Noise reduction, EQ, normalize, trim, fades — all fine"
    : mediaKind === "video" ? "Color grading, stabilization, denoise, trim, crop — all fine"
    : "Color grading, sharpening, denoise, upscaling, crop, rotate — all fine";
  const formatHint =
    mediaKind === "audio" ? "WAV, MP3, M4A, AAC, FLAC and other audio formats supported"
    : mediaKind === "video" ? "MP4, MOV, HEVC and other common video formats supported"
    : "JPEG, PNG, HEIC and camera RAW supported";
  const matchingNoun = mediaKind === "audio" ? "recordings" : mediaKind === "video" ? "videos" : "images";
  const flaggedLabel = mediaKind === "audio" ? "Flagged section:" : mediaKind === "video" ? "Flagged region:" : "Flagged region (red):";

  const dynamicAlertTitle = verification?.rejectionCode 
    ? verification.rejectionCode.replace(/_/g, ' ') 
    : "SCREENSHOT DETECTED";
return (
<div
  className="fixed inset-0 z-[70] flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-md p-4 pt-16 sm:pt-4 overflow-y-auto overscroll-contain [&::-webkit-scrollbar]:hidden"
  style={{ height: "100dvh", scrollbarWidth: "none", msOverflowStyle: "none" }}
  onClick={onClose}
>    
  <div
        className="w-full max-w-[768px] rounded-2xl bg-white dark:bg-[#0f0f0f] text-black dark:text-white border border-black dark:border-white shadow-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-gray-200 dark:border-white/10 px-6 py-5 md:px-8 md:py-6 shrink-0">
          <div className="min-w-0 flex flex-col gap-1">
            <h2 className="text-xl md:text-2xl font-medium font-['Product_Sans'] text-black dark:text-white">Verify with original</h2>
            {mediaName && <div className="text-[11px] text-gray-500 uppercase tracking-widest font-['Product_Sans'] truncate">FILE: {mediaName}</div>}
          </div>
          <button
            className="h-8 w-8 mt-0.5 flex items-center justify-center rounded-full text-black dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition"
            onClick={onClose} aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>

<div   className="p-6 md:p-8 space-y-6 overflow-y-auto overscroll-contain [&::-webkit-scrollbar]:hidden"
  style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
            {step === "loading" && (
            <div className="py-12 text-center text-gray-500 font-['Product_Sans']"><Spinner /> <span className="ml-2">Loading…</span></div>
          )}

          {/* ── INTRO ── */}
          {step === "intro" && (
            <>
              {/* Alert Box */}
              <div className="relative rounded-lg border border-[#4A2222] dark:border-red-500/30 bg-[#FFEBEB] dark:bg-[#1A0F0F] p-6 overflow-hidden">
                <div className="absolute left-0 top-0 bottom-0 w-[5px] bg-red-600 dark:bg-red-500"></div>
                
                <div className="flex items-center gap-2 mb-3 text-red-600 dark:text-red-500 text-sm font-bold uppercase tracking-widest font-['Product_Sans']">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                  {dynamicAlertTitle}
                </div>
                
                <div className="text-xl font-medium text-black dark:text-white mb-3 font-['Product_Sans']">We couldn't validate this media</div>
                <p className="text-sm text-gray-800 dark:text-gray-300 leading-relaxed font-['Product_Sans']">
                  To validate and register your media, upload the original {kindNoun} directly
                  from the device it was captured on, or transfer it using Bluetooth, AirDrop, Quick Share, or a USB cable.
                </p>
              </div>

              {/* Criteria Box */}
              <div className="rounded-lg border border-gray-300 dark:border-white/20 bg-transparent p-6 space-y-4">
                <div className="text-sm font-bold uppercase tracking-widest text-black dark:text-white font-['Product_Sans']">Verification Criteria</div>
                <ul className="space-y-4 text-sm text-gray-700 dark:text-gray-300 font-['Product_Sans']">
                  <li className="flex items-start gap-3">
                    <svg className="w-[18px] h-[18px] text-emerald-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                    <span>{editExamples}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-[18px] h-[18px] text-emerald-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                    <span>Your original stays private; it's used only for verification</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-[18px] h-[18px] text-red-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    <span>Added / removed / replaced content will not pass</span>
                  </li>
                </ul>
              </div>

              {attemptCount > 0 && (
                <div className="rounded-lg border border-yellow-500/30 bg-yellow-50 dark:bg-yellow-500/10 px-4 py-3 text-sm text-yellow-800 dark:text-yellow-200/90 font-['Product_Sans']">
                  This replaces the original you uploaded before.
                  {retriesLeft > 0 && <> You have {retriesLeft} {retriesLeft === 1 ? "try" : "tries"} left.</>}
                </div>
              )}
              
              {errorMsg && (
                <div className="rounded-lg border border-red-500/30 bg-red-50 dark:bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-300 font-['Product_Sans']">{errorMsg}</div>
              )}

              <input
                ref={fileInput} type="file" className="hidden"
                accept={mediaKind === "video" ? "video/*" : mediaKind === "audio" ? "audio/*" : "image/*,.heic,.heif,.dng,.raw"}
                onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
              />
              
              <div className="pt-2">
                <button
                  className="w-full py-4 rounded-lg bg-black text-white dark:bg-white dark:text-black font-bold uppercase tracking-widest text-sm hover:opacity-90 transition disabled:opacity-60 disabled:cursor-not-allowed font-['Product_Sans']"
                  onClick={pickFile}
                  disabled={attaching}
                >
                  {attaching ? <><Spinner size={16} /> <span className="ml-2">PREPARING…</span></> : "CHOOSE ORIGINAL FILE"}
                </button>
                <p className="text-[10px] uppercase tracking-widest text-gray-500 text-center font-bold font-['Product_Sans'] mt-4">{formatHint}</p>
              </div>
            </>
          )}

          {/* ── NO ORIGINAL — reason to support ── */}
          {step === "noOriginal" && (
            <>
              <div className="rounded-lg border border-gray-300 dark:border-[#333] bg-transparent p-6 space-y-3">
                <div className="text-lg font-semibold text-black dark:text-white font-['Product_Sans']">Don't have the original?</div>
                <p className="text-sm text-gray-700 dark:text-gray-400 leading-relaxed font-['Product_Sans']">
                  Tell us why and our team will review it manually. This goes to support — we'll follow up by email.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {NO_ORIGINAL_SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => setNoOrigReason(s)}
                    className="px-4 py-2 rounded-full border border-gray-300 dark:border-white/15 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/10 transition text-left font-['Product_Sans']">
                    {s}
                  </button>
                ))}
              </div>
              <textarea
                className="w-full rounded-lg bg-white dark:bg-black/30 border border-gray-300 dark:border-white/10 px-4 py-3 text-sm text-black dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-black dark:focus:border-white/25 min-h-[120px] font-['Product_Sans']"
                placeholder="e.g. This is a promotional video an agency produced for us…"
                value={noOrigReason}
                onChange={(e) => setNoOrigReason(e.target.value)}
              />
              {errorMsg && <div className="text-sm text-red-500 dark:text-red-400 font-['Product_Sans']">{errorMsg}</div>}
              <div className="flex gap-4">
                <button
                  className="flex-1 py-4 rounded-lg border border-gray-300 dark:border-white/15 text-black dark:text-gray-200 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-white/10 transition font-['Product_Sans']"
                  onClick={() => { setErrorMsg(null); setStep("intro"); }}
                >
                  Back
                </button>
                <button
                  className="flex-1 py-4 rounded-lg bg-black text-white dark:bg-white dark:text-black font-semibold text-sm hover:opacity-90 transition disabled:opacity-50 font-['Product_Sans']"
                  onClick={submitNoOriginal} disabled={submittingNoOrig || !noOrigReason.trim()}
                >
                  {submittingNoOrig ? <><Spinner size={14} /> <span className="ml-1.5">Sending…</span></> : "Send to support"}
                </button>
              </div>
            </>
          )}

          {/* ── NO ORIGINAL — sent ── */}
          {step === "noOriginalSent" && (
            <div className="py-12 px-6 text-center space-y-6 rounded-xl border border-gray-300 dark:border-[#333] bg-transparent">
              <div className="mx-auto w-20 h-20 rounded-full bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-3xl text-emerald-500">✓</div>
              <div>
                <div className="text-xl font-semibold text-emerald-600 dark:text-emerald-400 font-['Product_Sans']">Sent to support</div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 leading-relaxed font-['Product_Sans']">
                  Thanks — our team will review your request and get back to you by email.
                </p>
              </div>
              <button className="w-full py-4 mt-2 rounded-lg bg-black text-white dark:bg-white dark:text-black font-semibold hover:opacity-90 transition font-['Product_Sans']" onClick={onClose}>
                Done
              </button>
            </div>
          )}

          {/* ── UPLOADING ── */}
          {step === "uploading" && (
            <div className="py-12 px-6 space-y-6 rounded-xl border border-gray-300 dark:border-[#333] bg-transparent">
              <div className="text-center text-sm font-medium text-black dark:text-gray-200 font-['Product_Sans']">Uploading original… {progress}%</div>
              <div className="h-2.5 rounded-full bg-gray-200 dark:bg-white/10 overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-gray-500 text-center font-['Product_Sans']">Uploading securely — don't close this window</p>
            </div>
          )}

          {/* ── MATCHING ── */}
          {step === "matching" && (
            <div className="py-12 px-6 text-center space-y-6 rounded-xl border border-gray-300 dark:border-[#333] bg-transparent">
              <div className="mx-auto w-20 h-20 rounded-full border border-gray-300 dark:border-white/10 bg-gray-50 dark:bg-white/5 flex items-center justify-center text-black dark:text-white">
                <Spinner size={34} />
              </div>
              <div>
                <div className="text-xl font-semibold text-black dark:text-white font-['Product_Sans']">Comparing with your original…</div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 leading-relaxed font-['Product_Sans']">
                  We're aligning both {matchingNoun} and checking that every change is an
                  enhancement — not new content. This usually takes under a minute.
                </p>
              </div>
              <p className="text-sm text-gray-500 font-['Product_Sans']">You can close this window — we'll keep processing.</p>
            </div>
          )}

          {/* ── PASSED ── */}
          {step === "passed" && (
            <div className="py-12 px-6 text-center space-y-6 rounded-xl border border-gray-300 dark:border-[#333] bg-transparent">
              <div className="mx-auto w-20 h-20 rounded-full bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-3xl text-emerald-500">✓</div>
              <div>
                <div className="text-xl font-semibold text-emerald-600 dark:text-emerald-400 font-['Product_Sans']">Original matched — registering</div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 leading-relaxed font-['Product_Sans']">
                  The original you uploaded matched the media, and your edited media is
                  now being registered. It'll show as registered in your library shortly.
                </p>
              </div>
              {verification?.percentChanged != null && (
                <div className="text-sm font-medium text-gray-500 font-['Product_Sans']">Percentage of edit/changes found: {Number(verification.percentChanged).toFixed(1)}%</div>
              )}
              <button className="w-full py-4 mt-2 rounded-lg bg-black text-white dark:bg-white dark:text-black font-semibold hover:opacity-90 transition font-['Product_Sans']" onClick={goToLibrary}>
                Back to library
              </button>
            </div>
          )}

          {/* ── FAILED ── */}
          {step === "failed" && (
            <>
              {/* Reason */}
              <div className="rounded-xl border border-red-500/30 bg-red-50 dark:bg-red-500/10 p-6 space-y-3">
                <div className="text-lg font-semibold text-red-600 dark:text-red-400 font-['Product_Sans']">{verdictCopy.title}</div>
                {serverReasons.length > 0 ? (
                  <ul className="space-y-2">
                    {serverReasons.map((r, i) => (
                      <li key={i} className="flex gap-2 text-sm text-gray-800 dark:text-gray-200 leading-relaxed font-['Product_Sans']">
                        <span className="text-red-500 mt-0.5">•</span>
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed font-['Product_Sans']">{verdictCopy.body}</p>
                )}
              </div>

              {/* Match figures */}
              {(matchPct != null || verification?.confidence != null) && (
                <div className="grid grid-cols-2 gap-4">
                  {matchPct != null && (
                    <div className="rounded-xl border border-gray-300 dark:border-[#333] bg-transparent px-4 py-5 text-center">
                      <div className="text-3xl font-semibold text-black dark:text-white font-['Product_Sans']">{matchPct.toFixed(0)}%</div>
                      <div className="text-sm text-gray-500 font-['Product_Sans'] mt-2">matches your original</div>
                    </div>
                  )}
                  {changedPct != null && (
                    <div className="rounded-xl border border-gray-300 dark:border-[#333] bg-transparent px-4 py-5 text-center">
                      <div className="text-3xl font-semibold text-black dark:text-white font-['Product_Sans']">{changedPct.toFixed(1)}%</div>
                      <div className="text-sm text-gray-500 font-['Product_Sans'] mt-2">edit/changes found (limit {AUTO_MATCH_THRESHOLD}%)</div>
                    </div>
                  )}
                </div>
              )}

              {evidence.changed && (
                <div className="space-y-3">
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-400 font-['Product_Sans']">{flaggedLabel}</div>
                  <div className="rounded-xl overflow-hidden border border-gray-300 dark:border-[#333] bg-gray-100 dark:bg-black/40 flex justify-center p-2">
                    <img src={evidence.changed} alt="Flagged regions" className="max-h-64 w-auto object-contain rounded-lg" />
                  </div>
                </div>
              )}

              {/* Retry / Review Actions */}
              <div className="space-y-4 pt-4">
                {retriesLeft > 0 ? (
                  <button
                    className="w-full py-4 rounded-lg border border-gray-300 dark:border-white/15 text-black dark:text-white hover:bg-gray-50 dark:hover:bg-white/10 transition text-sm font-semibold font-['Product_Sans']"
                    onClick={() => { setErrorMsg(null); setStep("intro"); }}
                  >
                    Upload a different original ({retriesLeft} {retriesLeft === 1 ? "try" : "tries"} left)
                  </button>
                ) : (
                  <div className="rounded-xl border border-gray-300 dark:border-[#333] bg-transparent px-4 py-4 text-sm text-gray-500 text-center font-['Product_Sans']">
                    You've used all {maxAttempts} verification attempts. You can still request a manual review below.
                  </div>
                )}

                <div className="rounded-xl border border-gray-300 dark:border-[#333] bg-transparent p-6 space-y-4">
                  <div>
                    <div className="text-lg font-semibold text-black dark:text-white font-['Product_Sans']">Think this is wrong?</div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 font-['Product_Sans']">
                      Request a manual review — our team compares both files by hand and can approve the registration.
                    </p>
                  </div>
                  <textarea
                    className="w-full rounded-lg bg-white dark:bg-black/30 border border-gray-300 dark:border-white/10 px-4 py-3 text-sm text-black dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-black dark:focus:border-white/25 min-h-[100px] font-['Product_Sans']"
                    placeholder="Tell us about your edit (optional) — e.g. which app or filter you used…"
                    value={reviewMessage}
                    onChange={(e) => setReviewMessage(e.target.value)}
                  />
                  {errorMsg && <div className="text-sm text-red-500 dark:text-red-400 font-['Product_Sans']">{errorMsg}</div>}
                  <button
                    className="w-full py-4 rounded-lg bg-black text-white dark:bg-white dark:text-black font-semibold text-sm hover:opacity-90 transition disabled:opacity-50 font-['Product_Sans']"
                    onClick={submitReview} disabled={submittingReview}
                  >
                    {submittingReview ? <><Spinner size={14} /> <span className="ml-1.5">Requesting…</span></> : "Request manual review"}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ── REVIEW PENDING ── */}
          {step === "reviewPending" && (
            <div className="py-12 px-6 text-center space-y-6 rounded-xl border border-gray-300 dark:border-[#333] bg-transparent">
              <div className="mx-auto w-20 h-20 rounded-full bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center text-3xl text-yellow-500">⏳</div>
              <div>
                <div className="text-xl font-semibold text-yellow-600 dark:text-yellow-500 font-['Product_Sans']">Manual review requested</div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 leading-relaxed font-['Product_Sans']">
                  Our team will compare your original and the edited version and get back
                  to you. You'll see the outcome here and in your library.
                </p>
              </div>
              <button className="w-full py-4 mt-2 rounded-lg border border-gray-300 dark:border-white/15 text-black dark:text-white font-semibold hover:bg-gray-50 dark:hover:bg-white/10 transition font-['Product_Sans']" onClick={onClose}>
                Close
              </button>
            </div>
          )}

          {/* ── REVIEW DECIDED ── */}
          {step === "reviewDecided" && verification?.manualReview && (
            <div className="py-12 px-6 text-center space-y-6 rounded-xl border border-gray-300 dark:border-[#333] bg-transparent">
              {verification.manualReview.status === "APPROVED" ? (
                <>
                  <div className="mx-auto w-20 h-20 rounded-full bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-3xl text-emerald-500">✓</div>
                  <div className="text-xl font-semibold text-emerald-600 dark:text-emerald-400 font-['Product_Sans']">Approved by review</div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 font-['Product_Sans']">Your media has been registered.</p>
                </>
              ) : (
                <>
                  <div className="mx-auto w-20 h-20 rounded-full bg-red-50 dark:bg-red-500/10 border border-red-500/30 flex items-center justify-center text-3xl text-red-500">✕</div>
                  <div className="text-xl font-semibold text-red-600 dark:text-red-400 font-['Product_Sans']">Review declined</div>
                  {verification.manualReview.decision_notes && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed italic font-['Product_Sans']">"{verification.manualReview.decision_notes}"</p>
                  )}
                </>
              )}
              
              <div className="pt-2">
                {verification.manualReview.status === "APPROVED" ? (
                  <button className="w-full py-4 rounded-lg bg-black text-white dark:bg-white dark:text-black font-semibold hover:opacity-90 transition font-['Product_Sans']" onClick={goToLibrary}>
                    Back to library
                  </button>
                ) : (
                  <button className="w-full py-4 rounded-lg border border-gray-300 dark:border-white/15 text-black dark:text-white font-semibold hover:bg-gray-50 dark:hover:bg-white/10 transition font-['Product_Sans']" onClick={onClose}>
                    Close
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}